import { PERMISSIONS } from './capabilities'

// The route and navigation layers consume this single projection of effective
// backend read permissions. `deferred` records are deliberately explicit: the
// backend has no trustworthy functional permission contract until F74E.
export const ROUTE_ACCESS = Object.freeze({
  'mitigation-tracker': { anyOf: [PERMISSIONS.execution.view] },
  'risk-quantification': { anyOf: [PERMISSIONS.fair.view] },
  'sensitivity-analysis': { anyOf: [PERMISSIONS.fair.view] },
  'fair-scenarios': { anyOf: [PERMISSIONS.fair.view] },
  'risk-data': { anyOf: [PERMISSIONS.risk.view] },
  'investment-optimization': { anyOf: [PERMISSIONS.analytics.view, PERMISSIONS.fair.view] },
  'risk-owner': { anyOf: [PERMISSIONS.scenarioData.riskOwnerView] },
  'asset-type': { anyOf: [PERMISSIONS.scenarioData.assetTypeView] },
  asset: { anyOf: [PERMISSIONS.scenarioData.assetView] },
  'incident-type': { anyOf: [PERMISSIONS.scenarioData.incidentTypeView] },
  'board-dashboard': { anyOf: [PERMISSIONS.board.view] },
  'executive-dashboard': { anyOf: [PERMISSIONS.program.view, PERMISSIONS.benefits.view, PERMISSIONS.analytics.view] },
  'platform-settings': { anyOf: [PERMISSIONS.settings.view] },
  'administration-users': { systemAdministratorOnly: true },
  'administration-roles': { systemAdministratorOnly: true },
  'governance-framework': { anyOf: [PERMISSIONS.proposal.view, PERMISSIONS.program.view, PERMISSIONS.board.view] },
  'strategic-analysis': { anyOf: [PERMISSIONS.analytics.view, PERMISSIONS.fair.view] },
  'risk-dependencies': { anyOf: [PERMISSIONS.riskDependency.view, PERMISSIONS.riskDependencyType.view] },
  'risk-financing': { anyOf: [PERMISSIONS.fair.view, PERMISSIONS.financing.view] },
  'decision-center': { anyOf: [PERMISSIONS.analytics.view, PERMISSIONS.board.view] },
  'portfolio-decision': { anyOf: [PERMISSIONS.analytics.view, PERMISSIONS.board.view] },
  'portfolio-analysis': { anyOf: [PERMISSIONS.analytics.view] },
  'investment-governance': { anyOf: [PERMISSIONS.proposal.view, PERMISSIONS.program.view, PERMISSIONS.board.view] },
  'investment-monitoring': { anyOf: [PERMISSIONS.proposal.view, PERMISSIONS.program.view, PERMISSIONS.execution.view] },
  'capital-allocation-proposal': { anyOf: [PERMISSIONS.capitalAllocationProposal.view] },
  'board-decisions': { anyOf: [PERMISSIONS.board.view] },
  'investment-projects': { anyOf: [PERMISSIONS.project.view] },
  'investment-proposals': { anyOf: [PERMISSIONS.proposal.view] },
  'program-execution': { anyOf: [PERMISSIONS.execution.view] },
  'investment-programs': { anyOf: [PERMISSIONS.program.view] },
  'benefits-governance': { anyOf: [PERMISSIONS.benefits.view, PERMISSIONS.benefits.observedView, PERMISSIONS.benefits.evidenceView, PERMISSIONS.benefits.realizationView, PERMISSIONS.benefits.reassessmentView] },
})

// QA-V2-011's machine-backed route contract. Keep this projection explicit so
// a role bootstrap change cannot silently remove a canonical journey at the
// browser boundary. Permissions remain the authority used by the gate.
export const CANONICAL_ROUTE_CONTRACT = Object.freeze({
  BOARD_MEMBER: Object.freeze({ allow: ['board-dashboard', 'executive-dashboard', 'board-decisions'], deny: ['program-execution'] }),
  MANAGEMENT_EXECUTIVE: Object.freeze({ allow: ['executive-dashboard', 'investment-projects', 'investment-proposals', 'investment-programs', 'program-execution', 'benefits-governance'], deny: ['administration-users'] }),
  RISK_ANALYST: Object.freeze({ allow: ['risk-quantification', 'fair-scenarios', 'portfolio-analysis', 'investment-optimization', 'risk-financing'], deny: ['board-decisions', 'program-execution'] }),
  INVESTMENT_GOVERNANCE_OPERATOR: Object.freeze({ allow: ['investment-governance', 'investment-projects', 'investment-proposals', 'investment-programs'], deny: ['program-execution'] }),
  PROGRAM_OPERATOR: Object.freeze({ allow: ['investment-programs', 'program-execution', 'mitigation-tracker'], deny: ['benefits-governance', 'board-decisions'] }),
  FINANCE_OPERATOR: Object.freeze({ allow: ['investment-programs'], deny: ['board-decisions', 'program-execution'] }),
  EVIDENCE_RECORDER: Object.freeze({ allow: ['benefits-governance'], deny: ['board-decisions', 'program-execution'] }),
  EVIDENCE_REVIEWER: Object.freeze({ allow: ['benefits-governance'], deny: ['board-decisions', 'program-execution'] }),
  RIGO_SYSTEM_ADMINISTRATOR: Object.freeze({ allow: ['administration-users', 'administration-roles'], deny: [] }),
})

export const SIDEBAR_ROUTE_KEYS = Object.freeze({
  'execution-tracker': 'mitigation-tracker',
  'outcome-measurement': 'benefits-governance',
})

export function getRouteAccess(key) {
  return ROUTE_ACCESS[SIDEBAR_ROUTE_KEYS[key] || key]
}

export function canAccessRoute(access, key) {
  const policy = getRouteAccess(key)
  if (!policy || policy.deferred) return false
  if (access.isSystemAdministrator) return true
  if (policy.systemAdministratorOnly) return false
  return access.canAny(policy.anyOf)
}
